import { __extends } from "tslib";
import React, { Component } from "react";
import PropTypes from "prop-types";
import { StyleSheet, View, Text, Animated, Dimensions, TouchableWithoutFeedback, Easing, Keyboard } from "react-native";
var TOAST_MAX_WIDTH = 0.8;
var TOAST_ANIMATION_DURATION = 200;
var positions = {
  TOP: 20,
  BOTTOM: -20,
  CENTER: 0
};
var durations = {
  LONG: 3500,
  SHORT: 2000
};
var styles = StyleSheet.create({
  defaultStyle: {
    position: "absolute",
    left: 0,
    right: 0,
    justifyContent: "center",
    alignItems: "center"
  },
  containerStyle: {
    padding: 10,
    backgroundColor: "#000",
    opacity: 0.8,
    borderRadius: 5
  },
  shadowStyle: {
    shadowColor: "#000",
    shadowOffset: {
      width: 4,
      height: 4
    },
    shadowOpacity: 0.8,
    shadowRadius: 6,
    elevation: 10
  },
  textStyle: {
    fontSize: 16,
    color: "#fff",
    textAlign: "center"
  }
});
var ToastContainer = /** @class */function (_super) {
  __extends(ToastContainer, _super);
  function ToastContainer() {
    var _this = _super.apply(this, arguments) || this;
    _this.componentDidMount = function () {
      Dimensions.addEventListener("change", _this._windowChanged);
      if (_this.props.keyboardAvoiding) {
        Keyboard.addListener("keyboardDidChangeFrame", _this._keyboardDidChangeFrame);
      }
      if (_this.state.visible) {
        _this._showTimeout = setTimeout(function () {
          return _this._show();
        }, _this.props.delay);
      }
    };
    _this.componentDidUpdate = function (prevProps) {
      if (_this.props.visible !== prevProps.visible) {
        if (_this.props.visible) {
          clearTimeout(_this._showTimeout);
          clearTimeout(_this._hideTimeout);
          _this._showTimeout = setTimeout(function () {
            return _this._show();
          }, _this.props.delay);
        } else {
          _this._hide();
        }
        _this.setState({
          visible: _this.props.visible
        });
      }
    };
    _this.componentWillUnmount = function () {
      _this._hide();
      Dimensions.removeEventListener("change", _this._windowChanged);
      Keyboard.removeListener("keyboardDidChangeFrame", _this._keyboardDidChangeFrame);
    };
    _this._animating = false;
    _this._root = null;
    _this._hideTimeout = null;
    _this._showTimeout = null;
    _this._keyboardHeight = 0;
    _this._windowChanged = function (_a) {
      var window = _a.window;
      _this.setState({
        windowWidth: window.width,
        windowHeight: window.height
      });
    };
    _this._keyboardDidChangeFrame = function (_a) {
      var endCoordinates = _a.endCoordinates;
      _this.setState({
        keyboardScreenY: endCoordinates.screenY
      });
    };
    _this._show = function () {
      clearTimeout(_this._showTimeout);
      if (!_this._animating) {
        clearTimeout(_this._hideTimeout);
        _this._animating = true;
        _this._root.setNativeProps({
          pointerEvents: "auto"
        });
        _this.props.onShow && _this.props.onShow(_this.props.siblingManager);
        Animated.timing(_this.state.opacity, {
          toValue: _this.props.opacity,
          duration: _this.props.animation ? TOAST_ANIMATION_DURATION : 0,
          easing: Easing.out(Easing.ease),
          useNativeDriver: true
        }).start(function (_a) {
          var finished = _a.finished;
          if (finished) {
            _this._animating = !finished;
            _this.props.onShown && _this.props.onShown(_this.props.siblingManager);
            if (_this.props.duration > 0) {
              _this._hideTimeout = setTimeout(function () {
                return _this._hide();
              }, _this.props.duration);
            }
          }
        });
      }
    };
    _this._hide = function () {
      clearTimeout(_this._showTimeout);
      clearTimeout(_this._hideTimeout);
      if (!_this._animating) {
        if (_this._root) {
          _this._root.setNativeProps({
            pointerEvents: "none"
          });
        }
        if (_this.props.onHide) {
          _this.props.onHide(_this.props.siblingManager);
        }
        Animated.timing(_this.state.opacity, {
          toValue: 0,
          duration: _this.props.animation ? TOAST_ANIMATION_DURATION : 0,
          easing: Easing.in(Easing.ease),
          useNativeDriver: true
        }).start(function (_a) {
          var finished = _a.finished;
          if (finished) {
            _this._animating = false;
            _this.props.onHidden && _this.props.onHidden(_this.props.siblingManager);
          }
        });
      }
    };
    var window = Dimensions.get("window");
    _this.state = {
      visible: _this.props.visible,
      opacity: new Animated.Value(0),
      windowWidth: window.width,
      windowHeight: window.height,
      keyboardScreenY: window.height
    };
    return _this;
  }
  ToastContainer.prototype.render = function () {
    var _a;
    var _this = this;
    var props = this.props;
    var windowWidth = this.state.windowWidth;
    var offset = props.position;
    var _b = this.state,
      windowHeight = _b.windowHeight,
      keyboardScreenY = _b.keyboardScreenY;
    var keyboardHeight = Math.max(windowHeight - keyboardScreenY, 0);
    var position = offset ? (_a = {}, _a[offset < 0 ? "bottom" : "top"] = offset < 0 ? keyboardHeight - offset : offset, _a) : {
      top: 0,
      bottom: keyboardHeight
    };
    return this.state.visible || this._animating ? (/*#__PURE__*/React.createElement(View, {
      style: [styles.defaultStyle, position, props.customStyles],
      pointerEvents: "box-none"
    }, /*#__PURE__*/React.createElement(TouchableWithoutFeedback, {
      onPress: function () {
        if (typeof _this.props.onPress === "function") {
          _this.props.onPress();
        }
        if (_this.props.hideOnPress) {
          _this._hide();
        }
      }
    }, /*#__PURE__*/React.createElement(Animated.View, {
      style: [styles.containerStyle, {
        marginHorizontal: windowWidth * ((1 - TOAST_MAX_WIDTH) / 2)
      }, props.containerStyle, props.backgroundColor && {
        backgroundColor: props.backgroundColor
      }, {
        opacity: this.state.opacity
      }, props.shadow && styles.shadowStyle, props.shadowColor && {
        shadowColor: props.shadowColor
      }],
      pointerEvents: "none",
      ref: function (ele) {
        return _this._root = ele;
      }
    }, /*#__PURE__*/React.createElement(Text, {
      style: [styles.textStyle, props.textStyle, props.textColor && {
        color: props.textColor
      }]
    }, this.props.children))))) : null;
  };
  return ToastContainer;
}(Component);
ToastContainer.defaultProps = {
  visible: false,
  duration: durations.SHORT,
  animation: true,
  shadow: true,
  position: positions.BOTTOM,
  opacity: 0.8,
  delay: 0,
  hideOnPress: true,
  keyboardAvoiding: true
};
ToastContainer.propTypes = {
  containerStyle: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
  duration: PropTypes.number,
  visible: PropTypes.bool,
  position: PropTypes.number,
  animation: PropTypes.bool,
  shadow: PropTypes.bool,
  keyboardAvoiding: PropTypes.bool,
  backgroundColor: PropTypes.string,
  opacity: PropTypes.number,
  shadowColor: PropTypes.string,
  textColor: PropTypes.string,
  textStyle: PropTypes.oneOfType([PropTypes.array, PropTypes.object, PropTypes.string]),
  delay: PropTypes.number,
  hideOnPress: PropTypes.bool,
  onPress: PropTypes.func,
  onHide: PropTypes.func,
  onHidden: PropTypes.func,
  onShow: PropTypes.func,
  onShown: PropTypes.func
};
export default ToastContainer;
export { positions, durations };